import React, { useState, FormEvent, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AxiosError } from "axios";
import { toast } from "sonner";
import prodigy from "../../Assets/brand/ProdiogyIcon.svg";
import eyeOpen from "../../Assets/icons/eyeOpen.svg";
import eyeClosed from "../../Assets/icons/eyeClose.svg";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { resetPasswordApi as resetPasswordSetupApi } from "../../Apis/login";
import { resetPasswordApi } from "../../Apis/resetPassword";
import { setTokens } from "../../redux/slices/AuthSlice";
import { clearPasswordSetup } from "../../redux/slices/ResetPassword";
import backArrow from "../../Assets/icons/backArrow.svg";
interface LocationState {
  email?: string;
  code?: string;
}

/** Common sequential/keyboard patterns that must not appear in password (case-insensitive). */
const FORBIDDEN_SEQUENCES = [
  "012",
  "123",
  "234",
  "345",
  "456",
  "567",
  "678",
  "789",
  "098",
  "987",
  "876",
  "765",
  "654",
  "543",
  "432",
  "321",
  "210",
  "1234",
  "2345",
  "3456",
  "4567",
  "5678",
  "6789",
  "0123",
  "12345",
  "23456",
  "34567",
  "45678",
  "56789",
  "abc",
  "bcd",
  "cde",
  "def",
  "efg",
  "fgh",
  "ghi",
  "hij",
  "ijk",
  "jkl",
  "klm",
  "lmn",
  "mno",
  "nop",
  "opq",
  "pqr",
  "qrs",
  "rst",
  "stu",
  "tuv",
  "uvw",
  "vwx",
  "wxy",
  "xyz",
  "cba",
  "dcb",
  "edc",
  "fed",
  "gfe",
  "hgf",
  "ihg",
  "jih",
  "kji",
  "lkj",
  "mlk",
  "nml",
  "onm",
  "pon",
  "qpo",
  "rqp",
  "srq",
  "tsr",
  "uts",
  "vut",
  "wvu",
  "xwv",
  "yxw",
  "zyx",
  "qwer",
  "wert",
  "erty",
  "rtyu",
  "tyui",
  "yuio",
  "uiop",
  "asdf",
  "sdfg",
  "dfgh",
  "fghj",
  "ghjk",
  "hjkl",
  "jkl",
  "zxcv",
  "xcvb",
  "cvbn",
  "vbnm",
  "qwerty",
  "asdfgh",
  "zxcvbn",
  "111",
  "222",
  "333",
  "444",
  "555",
  "666",
  "777",
  "888",
  "999",
  "000",
  "aaa",
  "bbb",
  "ccc",
  "abcabc",
];

function hasSequence(password: string): boolean {
  if (password.length < 3) return false;
  const lower = password.toLowerCase();
  for (const seq of FORBIDDEN_SEQUENCES) {
    if (seq.length <= lower.length && lower.includes(seq)) return true;
  }
  for (let len = 3; len <= Math.min(6, lower.length); len++) {
    for (let i = 0; i <= lower.length - len; i++) {
      const step = lower.charCodeAt(i + 1) - lower.charCodeAt(i);
      if (step !== 1 && step !== -1) continue;
      const isLetter = (x: number) => x >= 97 && x <= 122;
      const isDigit = (x: number) => x >= 48 && x <= 57;
      const first = lower.charCodeAt(i);
      if (!isLetter(first) && !isDigit(first)) continue;
      let consecutive = true;
      for (let j = 1; j < len; j++) {
        const cur = lower.charCodeAt(i + j);
        const prev = lower.charCodeAt(i + j - 1);
        if (
          cur !== prev + step ||
          (isLetter(first) && !isLetter(cur)) ||
          (isDigit(first) && !isDigit(cur))
        ) {
          consecutive = false;
          break;
        }
      }
      if (consecutive) return true;
    }
  }
  for (let i = 0; i <= lower.length - 3; i++) {
    const ch = lower[i];
    if (lower[i + 1] === ch && lower[i + 2] === ch) return true;
  }
  return false;
}

const ResetPassword: React.FC = () => {
  const { passwordSetupToken } = useAppSelector((state) => state.passwordSetup);
  const dispatch = useAppDispatch();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string>("");
  const navigate = useNavigate();
  const location = useLocation();

  // Get email and code from navigation state (for forgot password flow)
  const locationState = location.state as LocationState | null;
  const email = locationState?.email || "";
  const code = locationState?.code || "";

  // Determine which flow we're in: passwordSetupToken (TwoFactorAuth flow) or email/code (ForgotPassword flow)
  const isPasswordSetupFlow = !!passwordSetupToken;
  const isForgotPasswordFlow = !!(email && code);
  const passwordValidationCriteria = useMemo(() => {
    const hasMinLength = newPassword.length >= 8;
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    const hasNoSequence = newPassword.length > 0 && !hasSequence(newPassword);

    return [
      {
        id: "minLength",
        label: "8 characters or more",
        isValid: hasMinLength,
      },
      {
        id: "lowercase",
        label: "1 lowercase letter",
        isValid: hasLowerCase,
      },
      {
        id: "uppercase",
        label: "1 uppercase letter",
        isValid: hasUpperCase,
      },
      {
        id: "number",
        label: "1 number",
        isValid: hasNumber,
      },
      {
        id: "specialChar",
        label: "1 special character",
        isValid: hasSpecialChar,
      },
      {
        id: "noSequence",
        label: "No sequences (e.g. 123, abc, qwerty, 111)",
        isValid: hasNoSequence,
      },
    ];
  }, [newPassword]);

  const confirmPasswordNoSequence =
    confirmPassword.length < 3 || !hasSequence(confirmPassword);

  const allCriteriaMet = passwordValidationCriteria.every(
    (criterion) => criterion.isValid
  );
  const passwordsMatch =
    confirmPassword.length > 0 && newPassword === confirmPassword;
  const passwordsMismatch =
    confirmPassword.length > 0 &&
    newPassword !== confirmPassword &&
    newPassword.length > 0;

  const isFormValid = (): boolean => {
    return (
      allCriteriaMet &&
      newPassword.length > 0 &&
      confirmPassword.length > 0 &&
      newPassword === confirmPassword &&
      confirmPasswordNoSequence
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isFormValid()) return;

    setIsSubmitting(true);
    setApiError("");

    try {
      // Flow 1: Password Setup Flow (from TwoFactorAuth)
      if (isPasswordSetupFlow && passwordSetupToken) {
        const response = await resetPasswordSetupApi({
          passwordSetupToken,
          newPassword,
          confirmPassword,
        });

        if (response.ok) {
          toast.success("Password reset successfully");
          dispatch(
            setTokens({
              accessToken: response.accessToken,
              refreshToken: response.refreshToken,
            })
          );
          dispatch(clearPasswordSetup());
          navigate("/my-leads");
        }
      }
      // Flow 2: Forgot Password Flow
      else if (isForgotPasswordFlow && email && code) {
        const response = await resetPasswordApi({
          email,
          code,
          newPassword,
        });

        if (response.ok) {
          toast.success("Password reset successfully");
          navigate("/");
        }
      }
    } catch (error) {
      console.error("Reset password failed:", error);

      if (error instanceof AxiosError) {
        const errorMessage =
          (error.response?.data as { error?: string })?.error ||
          error.message ||
          "An error occurred while resetting your password";

        setApiError(errorMessage);

        // If it's a session/token error, redirect to login
        if (error.response?.status === 401 || error.response?.status === 403) {
          toast.error("Session expired. Please log in again");
          navigate("/");
        } else {
          toast.error(errorMessage);
        }
      } else {
        const errorMessage = "An unexpected error occurred. Please try again.";
        setApiError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="w-full max-w-md h-full flex flex-col"
      id="reset-password-container"
    >
      <button
        type="button"
        className="mt-4 text-[14px] font-medium text-[#080F1A] font-outfit leading-[150%] flex items-center gap-2 rounded"
        onClick={() => navigate(-1)}
        aria-label="Go back to previous page"
        id="first-verify-back-button"
      >
        <img
          src={backArrow}
          alt=""
          aria-hidden="true"
          id="first-verify-back-arrow"
        />
        Go Back
      </button>
      <div
        className="w-full max-w-md h-full flex flex-col"
        id="reset-password-page"
      >
        <div
          className="flex-1 flex flex-col justify-center"
          id="reset-password-content-wrapper"
        >
          <img
            id="reset-password-logo"
            src={prodigy}
            alt="Prodigy Health"
            className="w-32 md:w-40 mb-8"
          />

          <h2
            className="text-[18px] font-medium text-[var(--Text-Black,#080F1A)] font-outfit leading-[120%] mb-2"
            id="reset-password-heading"
          >
            Reset Password
          </h2>

          <p
            className="text-[14px] font-normal text-[var(--Text-Light-Grey,#647491)] font-outfit leading-[150%] mb-8"
            id="reset-password-description"
          >
            Enter your new password below
          </p>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col space-y-5"
            id="reset-password-form"
          >
            <div
              className="relative flex flex-col"
              id="reset-password-new-password-wrapper"
            >
              <label
                htmlFor="reset-password-new-password-input"
                className="sr-only"
                id="reset-password-new-password-label"
              >
                New password
              </label>
              <input
                id="reset-password-new-password-input"
                type={showNewPassword ? "text" : "password"}
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-[#0566FF] focus:border-transparent"
                aria-label="New password"
                aria-required="true"
                aria-invalid={newPassword.length > 0 && newPassword.length < 8}
                aria-describedby="reset-password-validation"
              />
              <button
                id="reset-password-new-password-toggle"
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#0566FF] rounded"
                aria-label={showNewPassword ? "Hide password" : "Show password"}
              >
                <img
                  id="reset-password-new-password-toggle-icon"
                  src={showNewPassword ? eyeOpen : eyeClosed}
                  alt={showNewPassword ? "Hide password" : "Show password"}
                  className="w-5 h-5"
                  aria-hidden="true"
                />
              </button>
            </div>

            <div
              id="reset-password-validation"
              className="flex flex-col gap-2 mb-2"
              role="list"
              aria-label="Password requirements"
            >
              {/* First row: 2 validations */}
              <div className="flex gap-4" id="reset-password-validation-row-1">
                {passwordValidationCriteria.slice(0, 2).map((criterion) => (
                  <div
                    key={criterion.id}
                    id={`reset-password-validation-${criterion.id}`}
                    className="flex items-center gap-2 flex-1"
                    role="listitem"
                  >
                    {criterion.isValid ? (
                      <div
                        className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0"
                        id={`reset-password-validation-icon-${criterion.id}-valid`}
                      >
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="3"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4.5 12.75l6 6 9-13.5"
                          />
                        </svg>
                      </div>
                    ) : (
                      <div
                        className="w-5 h-5 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0"
                        id={`reset-password-validation-icon-${criterion.id}-invalid`}
                      >
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="3"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </div>
                    )}
                    <span
                      className={`text-xs font-normal font-outfit leading-[150%] ${
                        criterion.isValid ? "text-green-600" : "text-[#F94839]"
                      }`}
                      id={`reset-password-validation-label-${criterion.id}`}
                    >
                      {criterion.label}
                    </span>
                  </div>
                ))}
              </div>
              {/* Second row: 2 validations */}
              <div className="flex gap-4" id="reset-password-validation-row-2">
                {passwordValidationCriteria.slice(2, 4).map((criterion) => (
                  <div
                    key={criterion.id}
                    id={`reset-password-validation-${criterion.id}`}
                    className="flex items-center gap-2 flex-1"
                    role="listitem"
                  >
                    {criterion.isValid ? (
                      <div
                        className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0"
                        id={`reset-password-validation-icon-${criterion.id}-valid`}
                      >
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="3"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4.5 12.75l6 6 9-13.5"
                          />
                        </svg>
                      </div>
                    ) : (
                      <div
                        className="w-5 h-5 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0"
                        id={`reset-password-validation-icon-${criterion.id}-invalid`}
                      >
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="3"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </div>
                    )}
                    <span
                      className={`text-xs font-normal font-outfit leading-[150%] ${
                        criterion.isValid ? "text-green-600" : "text-[#F94839]"
                      }`}
                      id={`reset-password-validation-label-${criterion.id}`}
                    >
                      {criterion.label}
                    </span>
                  </div>
                ))}
              </div>
              {/* Third row: 2 validations (special char + no sequence) */}
              <div className="flex gap-4" id="reset-password-validation-row-3">
                {passwordValidationCriteria.slice(4, 6).map((criterion) => (
                  <div
                    key={criterion.id}
                    id={`reset-password-validation-${criterion.id}`}
                    className="flex items-center gap-2 flex-1"
                    role="listitem"
                  >
                    {criterion.isValid ? (
                      <div
                        className="w-5 h-5 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0"
                        id={`reset-password-validation-icon-${criterion.id}-valid`}
                      >
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="3"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4.5 12.75l6 6 9-13.5"
                          />
                        </svg>
                      </div>
                    ) : (
                      <div
                        className="w-5 h-5 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0"
                        id={`reset-password-validation-icon-${criterion.id}-invalid`}
                      >
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="3"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </div>
                    )}
                    <span
                      className={`text-xs font-normal font-outfit leading-[150%] ${
                        criterion.isValid ? "text-green-600" : "text-[#F94839]"
                      }`}
                      id={`reset-password-validation-label-${criterion.id}`}
                    >
                      {criterion.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div
              className="relative flex flex-col"
              id="reset-password-confirm-password-wrapper"
            >
              <label
                htmlFor="reset-password-confirm-password-input"
                className="sr-only"
                id="reset-password-confirm-password-label"
              >
                Confirm password
              </label>
              <input
                id="reset-password-confirm-password-input"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                disabled={!allCriteriaMet}
                className={`w-full rounded-lg border px-4 py-3 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-[#0566FF] focus:border-transparent ${
                  !allCriteriaMet
                    ? "bg-gray-100 border-gray-300 cursor-not-allowed opacity-60"
                    : passwordsMismatch
                      ? "border-red-300"
                      : passwordsMatch
                        ? "border-green-300"
                        : "border-gray-300"
                }`}
                aria-label="Confirm password"
                aria-required="true"
                aria-invalid={passwordsMismatch}
                aria-disabled={!allCriteriaMet}
                aria-describedby={
                  confirmPassword.length > 0
                    ? "reset-password-match"
                    : undefined
                }
              />
              <button
                id="reset-password-confirm-password-toggle"
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#0566FF] rounded"
                aria-label={
                  showConfirmPassword ? "Hide password" : "Show password"
                }
              >
                <img
                  id="reset-password-confirm-password-toggle-icon"
                  src={showConfirmPassword ? eyeOpen : eyeClosed}
                  alt={showConfirmPassword ? "Hide password" : "Show password"}
                  className="w-5 h-5"
                  aria-hidden="true"
                />
              </button>
            </div>

            {confirmPassword.length > 0 && (
              <div
                id="reset-password-match"
                className={`text-xs font-normal font-outfit ${
                  passwordsMatch ? "text-green-600" : "text-red-600"
                }`}
                role="status"
                aria-live="polite"
              >
                {passwordsMatch ? "Passwords match" : "Passwords do not match"}
              </div>
            )}

            {apiError && (
              <div
                id="reset-password-error"
                className="text-xs font-normal font-outfit text-red-600"
                role="alert"
                aria-live="assertive"
              >
                {apiError}
              </div>
            )}

            <button
              id="reset-password-submit-button"
              type="submit"
              disabled={!isFormValid() || isSubmitting}
              className="w-full bg-[#0566FF] text-white text-center text-[14px] font-medium font-outfit leading-[150%] py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Continue"
            >
              {isSubmitting ? "Please wait..." : "Continue"}
            </button>
          </form>
        </div>

        <p
          className="mt-auto mb-10 text-xs text-[#647491]"
          id="reset-password-footer"
        >
          © 2025 Arete Healthtech Pvt. Ltd. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
